/**
 * Extension helpers for x402x
 * Implements x402x-router-settlement extension for PaymentRequired.extensions
 */

/**
 * Router settlement extension info
 * Contains all settlement-specific parameters that were previously in extra
 */
export interface RouterSettlementExtensionInfo {
  /** Schema version for the extension */
  schemaVersion: number;
  /** Optional description of the extension */
  description?: string;
  /** Unique salt for idempotency (32 bytes hex, dynamically generated) */
  salt?: string;
  /** Settlement router contract address */
  settlementRouter?: string;
  /** Hook contract address */
  hook?: string;
  /** Encoded hook parameters (hex string) */
  hookData?: string;
  /** Final recipient address (renamed from payTo to avoid confusion with accepts[].payTo) */
  finalPayTo?: string;
  /** Facilitator fee amount in token's smallest unit */
  facilitatorFee?: string;
}

/**
 * Router settlement extension structure
 * Location: PaymentRequired.extensions["x402x-router-settlement"]
 */
export interface RouterSettlementExtension {
  /** Extension information */
  info: RouterSettlementExtensionInfo;
  /** Optional JSON schema for validation */
  schema?: Record<string, unknown>;
}

/**
 * Create x402x-router-settlement extension declaration
 *
 * This extension informs clients that the server supports router settlement functionality.
 * Clients MUST echo extensions in their payment payload.
 *
 * @param params - Extension parameters
 * @param params.description - Optional description of the extension
 * @param params.schema - Optional JSON schema for validation
 * @param params.salt - Optional salt (will be auto-generated by enrichDeclaration if not provided)
 * @param params.settlementRouter - Settlement router contract address
 * @param params.hook - Hook contract address
 * @param params.hookData - Encoded hook parameters
 * @param params.finalPayTo - Final recipient address
 * @param params.facilitatorFee - Facilitator fee amount
 * @returns Extension object for PaymentRequired.extensions["x402x-router-settlement"]
 *
 * @example
 * ```typescript
 * const extension = createRouterSettlementExtension({
 *   description: "Settlement router with atomic fee distribution",
 *   settlementRouter: "0x...",
 *   hook: "0x...",
 *   hookData: "0x",
 *   finalPayTo: "0x...",
 *   facilitatorFee: "0",
 *   salt: "0x..." // Optional, will be auto-generated if not provided
 * });
 *
 * const paymentRequired = {
 *   x402Version: 2,
 *   resource: { url: "/api/payment", ... },
 *   accepts: [...],
 *   extensions: {
 *     "x402x-router-settlement": extension
 *   }
 * };
 * ```
 */
export function createRouterSettlementExtension(params?: {
  description?: string;
  schema?: Record<string, unknown>;
  salt?: string; // Optional salt, will be auto-generated if not provided
  settlementRouter?: string;
  hook?: string;
  hookData?: string;
  finalPayTo?: string;
  facilitatorFee?: string;
}): RouterSettlementExtension {
  const info: RouterSettlementExtensionInfo = {
    schemaVersion: 1,
  };

  // Add optional fields
  if (params?.description !== undefined) {
    info.description = params.description;
  }

  // Add salt if explicitly provided (otherwise enrichDeclaration will generate it)
  if (params?.salt) {
    info.salt = params.salt;
  }

  // Add settlement parameters if provided
  if (params?.settlementRouter) info.settlementRouter = params.settlementRouter;
  if (params?.hook) info.hook = params.hook;
  if (params?.hookData) info.hookData = params.hookData;
  if (params?.finalPayTo) info.finalPayTo = params.finalPayTo;
  // Only add facilitatorFee if explicitly provided (undefined = let facilitator calculate)
  if (params?.facilitatorFee !== undefined) info.facilitatorFee = params.facilitatorFee;

  // Create schema if not provided but we have settlement params
  let schema = params?.schema;
  if (!schema && params?.settlementRouter) {
    schema = {
      type: "object",
      properties: {
        schemaVersion: { type: "number" },
        description: { type: "string" },
        salt: { type: "string", pattern: "^0x[a-fA-F0-9]{64}$" },
        settlementRouter: { type: "string", pattern: "^0x[a-fA-F0-9]{40}$" },
        hook: { type: "string", pattern: "^0x[a-fA-F0-9]{40}$" },
        hookData: { type: "string", pattern: "^0x[a-fA-F0-9]*$" },
        finalPayTo: { type: "string", pattern: "^0x[a-fA-F0-9]{40}$" },
        facilitatorFee: { type: "string" },
      },
      // Salt is required in the final enriched version
      // facilitatorFee is optional (facilitator will calculate if missing)
      required: ["schemaVersion", "salt", "settlementRouter", "hook", "hookData", "finalPayTo"],
    };
  }

  return {
    info,
    ...(schema !== undefined && { schema }),
  };
}

/**
 * Get the extension key for router settlement
 *
 * @returns The extension key "x402x-router-settlement"
 */
export function getRouterSettlementExtensionKey(): string {
  return "x402x-router-settlement";
}
